/** Системы на экране «Объёмы» (замечание ПТО №3, выпуск 5): система строки из
 *  `raw.system` (разбор выпуска 3, разделы ОВ, ВК, НВ, НК), вид «По системам»
 *  (система ▶ раздел ▶ строки в порядке РД), доли систем строки вида «Сводно ▶».
 *  В прочих комплектах вид «По системам» группирует строки по `raw.group`
 *  (заголовок группы таблицы).
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`volumes/systems.test.mjs`), а Node не знает путей-алиасов. */
import type { PtoMaterialRow, PtoSystemShare, PtoVolumePosition } from '@shared/api';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Подпись группы без системы. */
export const NOT_DEFINED = 'не определена';
/** Подпись группы без заголовка группы таблицы (вид «По системам» в прочих
 *  комплектах). */
export const NO_GROUP = 'без группы';

/** Вид экрана: строки в порядке РД, по системам (или группам), сводно. */
export type VolumesView = 'rows' | 'systems' | 'summary';

// ── Система строки ──

/** Система строки: группа — комплект и коды («В1» ОВ2 ≠ «В1» ВК1). */
export interface VolumeSystem {
  /** «<шифр>|<коды через запятую>»; у «не определена» кодов нет — «<шифр>|». */
  key: string;
  /** Метка как в РД («В1», «В25, В26») или «не определена». */
  label: string;
  code: string | null;
  codes: string[];
  doc: string;
  /** Сомнение разметки (`not_in_registry` | `numbering` | `page_carry`); null — нет. */
  doubt: string | null;
  /** Откуда система (`own_pos`, `vor_ref`, `pos_heading`, …); null — нет. */
  source: string | null;
}

/** Ключ группы системы — как у сервера (`SystemShare.key`). */
export function systemKey(doc: string, codes: readonly string[]): string {
  return `${doc}|${codes.join(',')}`;
}

/** Система строки из `raw.system`; null — разметки у строки нет. Метка
 *  нескольких систем («В25, В26») — одна группа. */
export function volumeSystem(raw: unknown): VolumeSystem | null {
  const s = asObj(asObj(raw)?.system);
  if (!s) return null;
  const code = text(s.code);
  const doc = text(s.doc) ?? '';
  let codes = Array.isArray(s.codes)
    ? s.codes.map(text).filter((c): c is string => c !== null)
    : [];
  if (!code) codes = [];
  else if (codes.length === 0) codes = [code];
  return {
    key: systemKey(doc, codes),
    label: code ?? NOT_DEFINED,
    code,
    codes,
    doc,
    doubt: text(s.doubt),
    source: text(s.source),
  };
}

/** Сомнение разметки по-русски. */
export function systemDoubtText(doubt: string): string {
  switch (doubt) {
    case 'not_in_registry':
      return 'кода нет в перечне систем комплекта';
    case 'numbering':
      return 'нумерация ушла в раздел без заголовка системы';
    case 'page_carry':
      return 'подпись системы перешла с прошлого листа';
    default:
      return doubt;
  }
}

/** В строках есть разметка систем (раздел ОВ, ВК, НВ, НК после волны). */
export function hasSystems(items: readonly Pick<PtoVolumePosition, 'raw'>[]): boolean {
  return items.some((p) => volumeSystem(p.raw) !== null);
}

/** Вид по умолчанию: «По системам» — если хотя бы у одной строки система
 *  распознана (есть код), иначе строки в порядке РД. Разметка, где у всех строк
 *  «не определена» (ОВ1: перечень систем есть, коды в строках не прочитаны), —
 *  строки: вид из одной группы «не определена» ничего не даёт. Графа «Система»,
 *  перечень систем и переключатель — по разметке как есть (`hasSystems`). */
export function defaultView(items: readonly Pick<PtoVolumePosition, 'raw'>[]): VolumesView {
  return items.some((p) => volumeSystem(p.raw)?.code != null) ? 'systems' : 'rows';
}

/** Вид, в котором показать строку из ссылки: в «Сводно ▶» строк РД нет — вид
 *  со строками по тому же правилу, что вид по умолчанию ([`defaultView`]:
 *  «По системам» — только при распознанной системе хотя бы у одной строки,
 *  иначе строки по порядку РД); в остальных видах — тот же вид. */
export function viewForLinkedRow(
  view: VolumesView,
  items: readonly Pick<PtoVolumePosition, 'raw'>[],
): VolumesView {
  return view === 'summary' ? defaultView(items) : view;
}

/** Заголовок группы таблицы строки (`raw.group`, «1. Хозяйственно-питьевой
 *  водопровод В1»); null — нет. */
export function volumeGroup(raw: unknown): string | null {
  return text(asObj(raw)?.group);
}

// ── Раздел (часть РД) ──

/** Раздел строки для вида «По системам» — подпись графы «Раздел»: часть РД с
 *  основной надписи листа («СП01 · Спецификация …»), без неё — код части
 *  (`doc_part`) или вид таблицы. */
export function sectionOf(p: Pick<PtoVolumePosition, 'raw' | 'doc_part' | 'source_role'>): string {
  const label = text(asObj(asObj(p.raw)?.sheet)?.label);
  if (label) return label;
  const part = p.doc_part?.trim();
  if (part) return part === 'ИИ' ? 'ИИ · позиции по тексту листов' : part;
  const r = asObj(p.raw);
  if (p.source_role === 'rd') return 'ИИ · позиции по тексту листов';
  if (r?.summary_table) return 'СП · сводная спецификация';
  if (r?.table_kind === 'vor') return 'ВР · ведомость объёмов работ';
  return 'ЧТЖ · таблицы чертежей';
}

// ── Группировка «По системам» ──

/** Поля строки, нужные группировке. */
export type GroupItem = Pick<PtoVolumePosition, 'id' | 'raw' | 'doc_part' | 'source_role'>;

/** Раздел внутри группы: строки — в порядке РД (порядок списка). */
export interface RowSection<T> {
  label: string;
  rows: T[];
}

/** Группа вида «По системам»: система (или группа таблицы) ▶ разделы ▶ строки. */
export interface RowGroup<T> {
  key: string;
  label: string;
  /** `system` — система; `undefined` — «не определена»; `group` — заголовок
   *  группы таблицы (прочие комплекты); `ungrouped` — без группы. */
  kind: 'system' | 'undefined' | 'group' | 'ungrouped';
  system: VolumeSystem | null;
  /** Строк в группе. */
  count: number;
  /** Строк с сомнением разметки системы. */
  doubt: number;
  /** Есть строки сводной спецификации (СП01 — основной документ). */
  inSummary: boolean;
  sections: RowSection<T>[];
}

/** Строки → группы вида «По системам». Есть разметка систем — по системе
 *  (`raw.system`; строка без неё в такой ревизии — «не определена», комплект —
 *  из разметки соседей), иначе — по заголовку группы таблицы (`raw.group`).
 *  Есть ли разметка — `bySystems`, по умолчанию по самим строкам; экран
 *  передаёт признак всей ревизии: фильтр (вкладка, «Только на проверку») может
 *  оставить только строки без `raw.system` (позиции ИИ), и группировка не
 *  должна уходить в группы таблиц, пока у экрана графа «Система».
 *  Порядок групп — как в сводной спецификации (СП01 — основной документ: первая
 *  строка группы в сводной), затем группы без строк сводной — по первой строке
 *  (порядок РД); «не определена» и «без группы» — последними. Внутри — разделы
 *  по первой строке и строки как пришли (порядок РД). */
export function groupRows<T extends GroupItem>(
  items: readonly T[],
  bySystems = hasSystems(items),
): RowGroup<T>[] {
  const revDoc = items.map((p) => volumeSystem(p.raw)?.doc).find((d) => d) ?? '';
  const groups = new Map<string, RowGroup<T>>();
  // Группа → порядок первой строки сводной спецификации.
  const summaryRank = new Map<string, number>();
  for (const p of items) {
    let key: string;
    let make: () => RowGroup<T>;
    let doubt = false;
    if (bySystems) {
      const sys = volumeSystem(p.raw) ?? {
        key: systemKey(revDoc, []),
        label: NOT_DEFINED,
        code: null,
        codes: [],
        doc: revDoc,
        doubt: null,
        source: null,
      };
      key = `sys:${sys.key}`;
      doubt = sys.doubt !== null;
      make = () => ({
        key,
        label: sys.label,
        kind: sys.code ? 'system' : 'undefined',
        system: sys,
        count: 0,
        doubt: 0,
        inSummary: false,
        sections: [],
      });
    } else {
      const g = volumeGroup(p.raw);
      key = g ? `grp:${g}` : 'grp:';
      make = () => ({
        key,
        label: g ?? NO_GROUP,
        kind: g ? 'group' : 'ungrouped',
        system: null,
        count: 0,
        doubt: 0,
        inSummary: false,
        sections: [],
      });
    }
    let group = groups.get(key);
    if (!group) {
      group = make();
      groups.set(key, group);
    }
    group.count += 1;
    if (doubt) group.doubt += 1;
    if (asObj(p.raw)?.summary_table && !summaryRank.has(key)) {
      summaryRank.set(key, summaryRank.size);
      group.inSummary = true;
    }
    const label = sectionOf(p);
    let section = group.sections.find((s) => s.label === label);
    if (!section) {
      section = { label, rows: [] };
      group.sections.push(section);
    }
    section.rows.push(p);
  }
  const last = (g: RowGroup<T>) => g.kind === 'undefined' || g.kind === 'ungrouped';
  const rank = (g: RowGroup<T>) => summaryRank.get(g.key) ?? Number.MAX_SAFE_INTEGER;
  // Сортировка устойчивая: без строк сводной (и при равенстве) — порядок РД.
  return [...groups.values()].sort(
    (a, b) => Number(last(a)) - Number(last(b)) || rank(a) - rank(b),
  );
}

/** Ключ группы, в которой строка `id` (для раскрытия группы строки из ссылки). */
export function groupKeyOf<T extends GroupItem>(
  groups: readonly RowGroup<T>[],
  id: string,
): string | null {
  for (const g of groups) {
    if (g.sections.some((s) => s.rows.some((r) => r.id === id))) return g.key;
  }
  return null;
}

// ── «Сводно ▶»: доли систем строки ведомости ──

/** Σ долей систем строки и совпадение с количеством строки (до 3 знаков). У
 *  строки без долей — null. */
export function shareSum(row: {
  quantity: PtoMaterialRow['quantity'];
  systems?: readonly Pick<PtoSystemShare, 'quantity'>[];
}): { sum: number; matches: boolean } | null {
  const shares = row.systems ?? [];
  if (shares.length === 0) return null;
  const sum = shares.reduce((acc, s) => acc + (s.quantity ?? 0), 0);
  return { sum, matches: Math.abs(sum - (row.quantity ?? 0)) < 5e-4 };
}

/** Строка доли для подсказки и снабжения: «В1 — 4 шт (стр. 55, 58)». */
export function shareText(
  s: Pick<PtoSystemShare, 'label' | 'quantity' | 'source_ref'>,
  unit: string | null,
): string {
  const qty =
    s.quantity === null || s.quantity === undefined
      ? '—'
      : s.quantity.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
  const u = unit?.trim() ? ` ${unit.trim()}` : '';
  const at = s.source_ref ? ` (${s.source_ref})` : '';
  return `${s.label} — ${qty}${u}${at}`;
}
